/**
 * FALLBACK registry for biomarker identification.
 *
 * Since the 2026-08-25 name-canon consolidation, the AUTHORITY for a
 * biomarker's patient-facing name and health category is
 * `biomarker_display_rules` (edited on the Biomarkers admin page, served to
 * SQL surfaces through the `biomarker_names` view). This file remains the
 * fallback for rows with no LOINC or no active rule, plus the
 * alias→canonical grouping used at ingest/merge and the LOINC hints for
 * resolveAssayLoinc. Fix a wrong name or bucket in the admin page, not here.
 *
 * Used by:
 *  - BE OHD merge (warehouse.ts) — looks up canonical name by LOINC so OHD
 *    imports land with `test_name` matching what the dashboard expects.
 *  - FE category bars (modules/patient-dashboard/utils/healthCategories.ts)
 *    — derives HEALTH_CATEGORIES so the dashboard's category-stat charts
 *    can group results.
 *  - Anywhere else that needs to map between LOINC, alias, canonical
 *    name, and category — `findBiomarker()` is the helper.
 *
 * Adding a new biomarker:
 *   - Required: `canonical` (the TitleCase name shown in the UI) and
 *     `category` (which health-category bucket it rolls up to).
 *   - Add `loinc` if any external system (OHD, lab feeds) returns it
 *     keyed by LOINC. The OHD merge prefers LOINC over name matching.
 *   - Add `aliases` for legacy ELIS test_names or any other input shape
 *     that should resolve to this biomarker.
 *
 * Removing a biomarker: also remove any seed/test data that references it.
 * Renaming canonical: prefer adding the old name to `aliases` so old data
 * keeps mapping in until it's reprocessed.
 */
export const HEALTH_CATEGORY_LABELS = {
    heart: 'Heart Health',
    metabolic: 'Metabolic Risk',
    'liver-kidney': 'Liver & Kidney',
    micronutrient: 'Vitamins & Nutrients',
    hormone: 'Hormone',
    other: 'Other',
};
export const BIOMARKERS = [
    // ── Heart Health ────────────────────────────────────────────────
    { canonical: 'HDL', loinc: '2085-9', category: 'heart', aliases: ['HDL-C', 'Direct HDL'] },
    { canonical: 'LDL', loinc: '13457-7', category: 'heart', aliases: ['LDLX-C'] },
    { canonical: 'Total Cholesterol', loinc: '2093-3', category: 'heart', aliases: ['Cholesterol', 'Cholesterol-C'] },
    { canonical: 'Triglycerides', loinc: '2571-8', category: 'heart', aliases: ['Triglycerides-C'] },
    { canonical: 'VLDL-C', loinc: '13458-5', category: 'heart' },
    { canonical: 'Cholesterol/HDL Ratio', loinc: '9830-1', category: 'heart' },
    { canonical: 'APO B', category: 'heart' },
    { canonical: 'Lipoprotein(a)', category: 'heart' },
    { canonical: 'CK', category: 'heart' },
    { canonical: 'CK-MB', category: 'heart' },
    { canonical: 'CRP', category: 'heart', aliases: ['hs-CRP', 'high sensitivity CRP'] },
    { canonical: 'Homocysteine', category: 'heart' },
    // ── Metabolic Risk ──────────────────────────────────────────────
    { canonical: 'Glucose', loinc: '2345-7', category: 'metabolic', aliases: ['glucose'] },
    { canonical: 'HbA1c', loinc: '4548-4', category: 'metabolic', aliases: ['Hemoglobin A1c'] },
    { canonical: 'Insulin', category: 'metabolic' },
    { canonical: 'Calcium', loinc: '17861-6', category: 'metabolic' },
    { canonical: 'Sodium', loinc: '2951-2', category: 'metabolic' },
    { canonical: 'Potassium', loinc: '2823-3', category: 'metabolic' },
    { canonical: 'Chloride', loinc: '2075-0', category: 'metabolic' },
    {
        canonical: 'Enzymatic CO2',
        loinc: '2028-9',
        category: 'metabolic',
        aliases: ['Carbon Dioxide (CO₂/Biocarbonate)'],
    },
    { canonical: 'Magnesium', category: 'metabolic' },
    { canonical: 'Phosphorus', category: 'metabolic' },
    { canonical: 'Uric Acid', category: 'metabolic' },
    // ── Liver & Kidney ──────────────────────────────────────────────
    { canonical: 'AST', loinc: '1920-8', category: 'liver-kidney' },
    { canonical: 'ALT', loinc: '1742-6', category: 'liver-kidney' },
    { canonical: 'ALP', loinc: '6768-6', category: 'liver-kidney', aliases: ['ALKP'] },
    { canonical: 'GGT', category: 'liver-kidney' },
    { canonical: 'Amylase', category: 'liver-kidney' },
    { canonical: 'Total Bilirubin', loinc: '1975-2', category: 'liver-kidney' },
    // Its own analyte, its own code. Listing it as a Total Bilirubin alias
    // made loinc:backfill stamp DBIL with 1975-2 on prod (2026-08-11).
    { canonical: 'Direct Bilirubin', loinc: '1968-7', category: 'liver-kidney' },
    { canonical: 'Albumin', loinc: '1751-7', category: 'liver-kidney' },
    { canonical: 'Total Protein', loinc: '2885-2', category: 'liver-kidney' },
    { canonical: 'BUN', loinc: '3094-0', category: 'liver-kidney' },
    { canonical: 'Creatinine', loinc: '2160-0', category: 'liver-kidney' },
    { canonical: 'BUN/Creat Ratio', loinc: '3097-3', category: 'liver-kidney' },
    { canonical: 'Globulin', loinc: '10834-0', category: 'liver-kidney', aliases: ['globulin'] },
    {
        canonical: 'A/G Ratio',
        loinc: '1759-0',
        category: 'liver-kidney',
        aliases: ['albumin_globulin_ratio', 'Albumin/Globulin Ratio'],
    },
    // ── Vitamins & Nutrients ────────────────────────────────────────
    { canonical: 'Ferritin', category: 'micronutrient' },
    { canonical: 'Folate', category: 'micronutrient' },
    { canonical: 'Iron', category: 'micronutrient' },
    { canonical: 'TIBC', category: 'micronutrient' },
    { canonical: 'Vitamin B12', category: 'micronutrient' },
    // 'Vitamin D' is the curated display-rule name; the alias keeps the
    // name-tier category fallback working for canon-renamed rows.
    { canonical: 'Vitamin D (OH-25)', category: 'micronutrient', aliases: ['Vitamin D'] },
    // ── Hormones ────────────────────────────────────────────────────
    { canonical: 'Cortisol', category: 'hormone' },
    { canonical: 'DHEA-S', category: 'hormone' },
    { canonical: 'Estradiol', category: 'hormone' },
    { canonical: 'Free Androgen Index', category: 'hormone' },
    { canonical: 'Free Testosterone', category: 'hormone' },
    { canonical: 'FSH', category: 'hormone' },
    { canonical: 'IGF-1', category: 'hormone' },
    { canonical: 'LH', category: 'hormone' },
    { canonical: 'Progesterone', category: 'hormone' },
    { canonical: 'Prolactin', category: 'hormone' },
    { canonical: 'SHBG', category: 'hormone' },
    { canonical: 'Testosterone', category: 'hormone' },
    { canonical: 'Total Testosterone', category: 'hormone' },
    { canonical: 'Bioavailable Testosterone', category: 'hormone' },
    { canonical: 'TSH', loinc: '11580-8', category: 'hormone', aliases: ['tsh'] },
    { canonical: 'Free T3', category: 'hormone', aliases: ['T3, Free'] },
    { canonical: 'Free T4', category: 'hormone', aliases: ['T4, Free'] },
    { canonical: 'Total T3', category: 'hormone' },
    { canonical: 'Total T4', category: 'hormone' },
    { canonical: 'T3 Uptake', category: 'hormone' },
    { canonical: "Men's Hormone Health", category: 'hormone' },
    { canonical: "Woman's Hormone Health", category: 'hormone' },
    // ── Other (CBC, immune & allergy panel, miscellaneous) ──────────
    // CBC entries use a numeric prefix in the canonical name to control
    // sort order in the dashboard's biomarker selector.
    { canonical: '01; WBC', category: 'other' },
    { canonical: '02; RBC', category: 'other' },
    { canonical: '03; HGB', category: 'other' },
    { canonical: '04; HCT', category: 'other' },
    { canonical: '05; MCV', category: 'other' },
    { canonical: '06; MCH', category: 'other' },
    { canonical: '07; MCHC', category: 'other' },
    { canonical: '08; PLT', category: 'other' },
    { canonical: '09; RDW', category: 'other' },
    { canonical: '10; MPV', category: 'other' },
    { canonical: '11; NEUT%', category: 'other' },
    { canonical: '12; LYMP%', category: 'other' },
    { canonical: '13; MONO%', category: 'other' },
    { canonical: '14; EOS%', category: 'other' },
    { canonical: '15; BASO%', category: 'other' },
    { canonical: '16; NEUT#', category: 'other' },
    { canonical: '17; LYMP#', category: 'other' },
    { canonical: '18; MONO#', category: 'other' },
    { canonical: '19; EOS#', category: 'other' },
    { canonical: '20; BASO#', category: 'other' },
    // Digestive
    { canonical: 'Lipase', category: 'other' },
    // Men's health
    { canonical: 'PSA, Total', category: 'other' },
    // Immune & allergy
    { canonical: '01; Total IgE', category: 'other' },
    { canonical: 'Almond', category: 'other' },
    { canonical: 'Cashew', category: 'other' },
    { canonical: 'Cat Dander', category: 'other' },
    { canonical: 'Dog Dander', category: 'other' },
    { canonical: 'Egg White', category: 'other' },
    { canonical: 'Hazelnut', category: 'other' },
    { canonical: 'Peanut', category: 'other' },
    { canonical: 'Salmon', category: 'other' },
    { canonical: 'Scallop', category: 'other' },
    { canonical: 'Sesame', category: 'other' },
    { canonical: 'Shrimp', category: 'other' },
    { canonical: 'Soybean', category: 'other' },
    { canonical: 'Tuna', category: 'other' },
    { canonical: 'Walnut', category: 'other' },
    { canonical: 'Wheat', category: 'other' },
    { canonical: "Cow's Milk", category: 'other' },
    { canonical: 'Cod Fish', category: 'other' },
    { canonical: 'Ana o 3', category: 'other' },
    { canonical: 'Ber e 1', category: 'other' },
    { canonical: 'Cor a 1', category: 'other' },
    { canonical: 'Cor a 14', category: 'other' },
    { canonical: 'Cor a 8', category: 'other' },
    { canonical: 'Cor a 9', category: 'other' },
    { canonical: 'Jug r 1', category: 'other' },
    { canonical: 'Jug r 3', category: 'other' },
];
/** Fast LOINC → biomarker lookup. Used by the BE OHD merge. */
export const BIOMARKER_BY_LOINC = new Map(BIOMARKERS.filter((b) => b.loinc != null).map((b) => [b.loinc, b]));
/** All known canonical names + aliases grouped by category. Used by FE matching. */
export const BIOMARKER_NAMES_BY_CATEGORY = (() => {
    const out = {
        heart: [],
        metabolic: [],
        'liver-kidney': [],
        micronutrient: [],
        hormone: [],
        other: [],
    };
    for (const b of BIOMARKERS) {
        out[b.category].push(b.canonical);
        if (b.aliases)
            out[b.category].push(...b.aliases);
    }
    return out;
})();
/**
 * Find the biomarker matching a given test_name OR test_code (LOINC).
 * Returns the registry entry, or undefined if neither matches.
 *
 * Lookup order:
 *   1. test_code as LOINC (most reliable; OHD always carries one).
 *   2. test_name exact match against canonical.
 *   3. test_name exact match against aliases.
 */
export function findBiomarker(testName, testCode) {
    if (testCode) {
        const byLoinc = BIOMARKER_BY_LOINC.get(testCode);
        if (byLoinc)
            return byLoinc;
    }
    if (!testName)
        return undefined;
    return BIOMARKERS.find((b) => b.canonical === testName || b.aliases?.includes(testName));
}
